import styled from 'foundations-theming/utils';
import PropTypes from 'prop-types';
import { useMemo, useCallback, useEffect } from 'react';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import partial from 'react-utils/partial';
import { CONTACT } from 'customer-data-objects/constants/ObjectTypes';
import TimelineFilterGroupV2 from './TimelineFilterGroupV2';
import TimelineFilterListV2 from './TimelineFilterListV2';
import { Checkbox } from 'foundations-components/transitional/components/Checkbox';
import { Popover } from 'foundations-components/transitional/components/Popover';
import CrmLogger from 'customer-data-tracking/loggers/CrmLogger';
import TimelineFilterButton from './TimelineFilterButton';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import { Link } from 'foundations-components/transitional/components/Link';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { ErrorMessage } from 'foundations-components/transitional/components/ErrorMessage';
import { getAllFiltersJS, getAllFilterGroupsJS, getSelectedFiltersJS } from 'crm-events-data';
import { View } from 'foundations-components/transitional/components/View';
import { Flex } from 'foundations-components/components/Flex';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const FilterContainer = styled.div.withConfig({
  displayName: "TimelineEventsFilterV2__FilterContainer",
  componentId: "sc-1qxzk8b-0"
})(["display:flex;flex-wrap:wrap;padding:", ";"], ({
  theme
}) => `${theme.space['0']} ${theme.space['200']} ${theme.space['300']}`);
function TimelineEventsFilterV2({
  error,
  filterGroups,
  handleOpenChange,
  handlePopoverToggle,
  handleUpdateSelectedEvents,
  isOpen,
  objectType
}) {
  useEffect(() => {
    if (!isOpen) return;
    const timeoutId = setTimeout(() => {
      const popoverBody = document.querySelector('.uiPopoverBody');
      if (popoverBody) {
        const preventWheel = e => {
          e.preventDefault();
          e.stopPropagation();
        };
        popoverBody.addEventListener('wheel', preventWheel, {
          passive: false
        });
        return () => {
          popoverBody.removeEventListener('wheel', preventWheel);
        };
      }
    }, 50);
    return () => {
      clearTimeout(timeoutId);
    };
  }, [isOpen]);
  const allFilters = getAllFiltersJS(filterGroups);
  const allFiltersGroups = getAllFilterGroupsJS(filterGroups);
  const allSelectedFilters = getSelectedFiltersJS(filterGroups);
  const isEveryFilterSelected = allFilters.length === allSelectedFilters.length;
  const handleLogUsage = useCallback((eventType, evt) => {
    const actionType = evt.target.checked ? 'enabled' : 'disabled';
    if (objectType !== CONTACT) {
      return;
    }
    CrmLogger.logRecordInteraction(objectType, {
      action: `Timeline filter ${actionType}`,
      subAction: eventType
    });
    CrmLogger.log('timelineInteractions', {
      action: `${actionType} filter`,
      subAction: eventType
    });
  }, [objectType]);
  const handleToggleAllFilters = useCallback(evt => {
    evt.stopPropagation();
    if (isEveryFilterSelected) {
      handleLogUsage('ALL', evt);
    }
    const newEventFilters = isEveryFilterSelected ? [] : allFilters;
    handleUpdateSelectedEvents(newEventFilters);
  }, [allFilters, handleLogUsage, handleUpdateSelectedEvents, isEveryFilterSelected]);
  const handleFilterChange = useCallback((filter, evt) => {
    const isFilterEnabled = evt.target.checked;
    evt.stopPropagation();
    const eventType = filter.internalName;
    handleLogUsage(eventType, evt);
    const newEventFilters = !isFilterEnabled ? allSelectedFilters.filter(internalName => internalName !== eventType) : [...allSelectedFilters, eventType];
    handleUpdateSelectedEvents(newEventFilters);
  }, [allSelectedFilters, handleLogUsage, handleUpdateSelectedEvents]);
  const handleGroupClick = useCallback((filterGroup, evt) => {
    const isFilterEnabled = evt.target.checked;
    evt.stopPropagation();
    if (!isFilterEnabled) {
      handleLogUsage(filterGroup.internalName, evt);
    }
    const filtersToChange = allFiltersGroups[filterGroup.internalName] || [];
    const newEventFilters = isFilterEnabled ? allSelectedFilters.concat(filtersToChange) : allSelectedFilters.filter(internalName => !filtersToChange.includes(internalName));
    handleUpdateSelectedEvents(newEventFilters);
  }, [allFiltersGroups, allSelectedFilters, handleLogUsage, handleUpdateSelectedEvents]);
  const getSelectedFiltersCountByGroup = useCallback(filterGroup => {
    return filterGroup.filters.filter(({
      selected
    }) => selected).length;
  }, []);
  const isGroupCheckboxChecked = useCallback(filterGroup => {
    const selectedCount = getSelectedFiltersCountByGroup(filterGroup);
    return selectedCount === filterGroup.filters.length;
  }, [getSelectedFiltersCountByGroup]);
  const isGroupCheckboxIndeterminate = useCallback(filterGroup => {
    const selectedCount = getSelectedFiltersCountByGroup(filterGroup);
    return selectedCount > 0 && selectedCount < filterGroup.filters.length;
  }, [getSelectedFiltersCountByGroup]);

  // communication and team activity are rendered together
  // to ensure they're rendered in the same column
  const renderCommunicationAndTeamActivity = useCallback(({
    communicationFilterGroup,
    teamActivityFilterGroup
  }) => {
    const hasTeamActivityFilters = !!(teamActivityFilterGroup && teamActivityFilterGroup.filters.length);
    return /*#__PURE__*/_jsxs(Flex, {
      flexDirection: "column",
      children: [/*#__PURE__*/_jsx(TimelineFilterGroupV2, {
        checked: isGroupCheckboxChecked(communicationFilterGroup),
        indeterminate: isGroupCheckboxIndeterminate(communicationFilterGroup),
        label: communicationFilterGroup.translatedName,
        handleGroupClick: partial(handleGroupClick, communicationFilterGroup),
        children: /*#__PURE__*/_jsx(TimelineFilterListV2, {
          filters: communicationFilterGroup.filters,
          handleFilterChange: handleFilterChange
        })
      }, communicationFilterGroup.internalName), hasTeamActivityFilters ? /*#__PURE__*/_jsx(TimelineFilterGroupV2, {
        checked: isGroupCheckboxChecked(teamActivityFilterGroup),
        indeterminate: isGroupCheckboxIndeterminate(teamActivityFilterGroup),
        label: teamActivityFilterGroup.translatedName,
        handleGroupClick: partial(handleGroupClick, teamActivityFilterGroup),
        children: /*#__PURE__*/_jsx(TimelineFilterListV2, {
          filters: teamActivityFilterGroup.filters,
          handleFilterChange: handleFilterChange
        })
      }, teamActivityFilterGroup.internalName) : null]
    }, "communication-and-team-activity");
  }, [handleFilterChange, handleGroupClick, isGroupCheckboxChecked, isGroupCheckboxIndeterminate]);
  const renderDropdownFilterOptions = useCallback(filterGroup => {
    const groupType = filterGroup.internalName;
    const filters = filterGroup.filters;
    const label = filterGroup.translatedName;
    if (objectType === CONTACT) {
      if (groupType === 'COMMUNICATION') {
        const teamActivityFilterGroup = filterGroups.find(({
          internalName
        }) => internalName === 'TEAMACTIVITY');
        return renderCommunicationAndTeamActivity({
          communicationFilterGroup: filterGroup,
          teamActivityFilterGroup
        });
      }
      if (groupType === 'TEAMACTIVITY') {
        return null;
      }
    }
    if (!filters.length) {
      return null;
    }
    return /*#__PURE__*/_jsx(TimelineFilterGroupV2, {
      checked: isGroupCheckboxChecked(filterGroup),
      indeterminate: isGroupCheckboxIndeterminate(filterGroup),
      label: label,
      handleGroupClick: partial(handleGroupClick, filterGroup),
      children: /*#__PURE__*/_jsx(TimelineFilterListV2, {
        filters: filters,
        handleFilterChange: handleFilterChange
      })
    }, groupType);
  }, [filterGroups, handleFilterChange, handleGroupClick, isGroupCheckboxChecked, isGroupCheckboxIndeterminate, objectType, renderCommunicationAndTeamActivity]);
  const renderedPopoverBody = useMemo(() => {
    if (error) {
      return /*#__PURE__*/_jsx(ErrorMessage, {
        inlineSize: "800px",
        paddingBlock: "600",
        illustrationProps: {
          width: 200
        },
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "timeline.filters.errorTitle"
        }),
        children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
          message: "timeline.filters.errorBody_jsx",
          options: {
            href: window.location.href
          },
          elements: {
            Link
          }
        })
      });
    }
    const isAnyFilterSelected = allSelectedFilters.length > 0;
    return [/*#__PURE__*/_jsx(View, {
      borderBlockEndStyle: "solid",
      borderBlockEndWidth: "100",
      borderBlockEndColor: "border-core-default",
      children: /*#__PURE__*/_jsx(Checkbox, {
        checked: isEveryFilterSelected,
        indeterminate: isAnyFilterSelected && !isEveryFilterSelected,
        paddingInlineStart: "300",
        paddingBlock: "400",
        "data-selenium-test-is-selected": isEveryFilterSelected,
        "data-selenium-test": "profile-timeline-filter__toggle-all-btn",
        onChange: handleToggleAllFilters,
        children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
          message: "timeline.filters.selectAll"
        })
      })
    }, "toggleAll"),
    /*#__PURE__*/
    /* @bend-expect trellis-tokenized */
    _jsx(FilterContainer, {
      children: filterGroups.map(renderDropdownFilterOptions)
    }, "filtersContainer")];
  }, [allSelectedFilters.length, error, filterGroups, handleToggleAllFilters, isEveryFilterSelected, renderDropdownFilterOptions]);
  const total = allFilters.length;
  const numSelectedFilters = allSelectedFilters.length;
  const PopoverBody = useCallback(() => {
    return /*#__PURE__*/_jsx(View, {
      padding: "0",
      children: renderedPopoverBody
    });
  }, [renderedPopoverBody]);
  return /*#__PURE__*/_jsx(Popover, {
    arrowSize: "none",
    autoPlacement: false,
    closeOnOutsideClick: true,
    onOpenChange: handleOpenChange,
    open: isOpen,
    placement: "bottom right",
    Content: PopoverBody,
    children: /*#__PURE__*/_jsx(TimelineFilterButton, {
      total: total,
      selected: numSelectedFilters,
      onClick: handlePopoverToggle
    })
  });
}
TimelineEventsFilterV2.propTypes = {
  error: PropTypes.object,
  filterGroups: PropTypes.array.isRequired,
  handleOpenChange: PropTypes.func.isRequired,
  handlePopoverToggle: PropTypes.func.isRequired,
  handleUpdateSelectedEvents: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  objectType: PropTypes.string.isRequired
};
export default TimelineEventsFilterV2;